import React, { useEffect, useRef, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { readOidcUserJson } from '../utils/oidcStorage';
import {
  clearTeamsAuthInProgress,
  markTeamsAuthInProgress,
  redirectToConnectWiseAuthorize,
  TEAMS_AUTH_IN_PROGRESS_KEY,
} from '../utils/teamsAuthFlow';
import { ensureTeamsInitialized, notifyTeamsAuthFailure, notifyTeamsAuthSuccess } from '../utils/teamsSdk';

export const TeamsAuthStartPage: React.FC = () => {
  const [startError, setStartError] = useState('');

  useEffect(() => {
    const redirectKey = 'cw-teams-auth-redirect-started';
    if (sessionStorage.getItem(redirectKey)) return;
    sessionStorage.setItem(redirectKey, '1');

    markTeamsAuthInProgress();

    void (async () => {
      try {
        await ensureTeamsInitialized();
        await redirectToConnectWiseAuthorize();
      } catch (error) {
        sessionStorage.removeItem(redirectKey);
        console.error('Teams auth start failed:', error);
        const message = error instanceof Error ? error.message : 'Unable to start ConnectWise SSO';
        setStartError(message);
        try {
          await notifyTeamsAuthFailure(message);
        } catch {
          // Error already shown on page.
        }
      }
    })();
  }, []);

  return (
    <div className="min-h-screen bg-slate-900 text-white flex items-center justify-center p-6">
      <div className="text-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-cyan-400 mx-auto mb-4"></div>
        <p className="text-sm text-slate-200">Redirecting to ConnectWise SSO…</p>
        <p className="text-xs text-slate-400 mt-2 max-w-sm">
          Sign in in this popup window. Teams will return you to the app when finished.
        </p>
        {startError && (
          <p className="mt-4 max-w-md text-sm text-red-200">{startError}</p>
        )}
      </div>
    </div>
  );
};

export const TeamsAuthCallbackPage: React.FC = () => {
  const auth = useAuth();
  const [callbackError, setCallbackError] = useState('');
  const completionRef = useRef(false);

  useEffect(() => {
    if (auth.isLoading || completionRef.current) return;
    if (!window.location.search.includes('code=') && !window.location.search.includes('error=')) {
      return;
    }

    if (!auth.isAuthenticated && !auth.error) {
      const complete = (auth as { signinRedirectCallback?: (url: string) => Promise<unknown> }).signinRedirectCallback;
      if (!complete) return;
      void complete(window.location.href).catch((error: unknown) => {
        completionRef.current = true;
        const message = error instanceof Error ? error.message : 'ConnectWise SSO failed';
        console.error('Teams auth callback failed:', error);
        notifyTeamsAuthFailure(message).catch(() => setCallbackError(message));
      });
    }
  }, [auth, auth.error, auth.isAuthenticated, auth.isLoading]);

  useEffect(() => {
    if (auth.isLoading || completionRef.current) return;

    if (auth.error) {
      completionRef.current = true;
      notifyTeamsAuthFailure(auth.error.message || 'ConnectWise SSO failed').catch((error) => {
        console.error(error);
        setCallbackError(error instanceof Error ? error.message : 'Unable to notify Teams of the sign-in failure.');
      });
      return;
    }

    if (auth.isAuthenticated) {
      completionRef.current = true;
      clearTeamsAuthInProgress();
      sessionStorage.removeItem('cw-teams-auth-redirect-started');
      const userJson = readOidcUserJson() || JSON.stringify(auth.user);
      notifyTeamsAuthSuccess(userJson).catch((error) => {
        console.error(error);
        setCallbackError('Sign-in completed. You can close this window and return to Teams.');
      });
    }
  }, [auth.error, auth.isAuthenticated, auth.isLoading]);

  return (
    <div className="min-h-screen bg-slate-900 text-white flex items-center justify-center p-6">
      <div className="text-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-cyan-400 mx-auto mb-4"></div>
        <p className="text-sm text-slate-200">Completing ConnectWise SSO…</p>
        {callbackError && (
          <p className="mt-4 max-w-md text-sm text-red-200">{callbackError}</p>
        )}
      </div>
    </div>
  );
};

export { TEAMS_AUTH_IN_PROGRESS_KEY };
